<template>
  <!-- Overlay for mobile -->
  <div
    v-if="mobileDevice && sidebarOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-[11000] md:hidden"
    @click="sidebarOpen = false"
  ></div>

  <aside
    class="bg-[#191919] h-full overflow-y-auto duration-150 z-[1]"
    :class="[
      mobileDevice && sidebarOpen ? 'fixed left-0 top-0 w-[80vw] max-w-xs h-screen md:hidden' : '',
      !mobileDevice ? 'relative w-full h-full' : '',
      'transition-all'
    ]"
    ref="sidebarRef"
  >
    <!-- Top bar: Logo + Hamburger (close) icon on mobile -->
    <div class="flex items-center justify-between px-4 py-[7.1px] sticky top-0 bg-[#191919] z-10">
      <router-link to="/" class="flex items-center gap-2">
        <img class="logo py-[6px] bg-[#191919] w-auto" 
        :class="mobileDevice? 'h-8' : 'h-12' "
        :src="Logo" alt="logo" />
      </router-link>
      <!-- Hamburger/close icon on mobile -->
      <button
        v-if="mobileDevice && sidebarOpen"
        class="md:hidden text-white text-2xl bg-[#292929] rounded-full p-2 hover:bg-[#444] transition ml-2"
        @click.stop="sidebarOpen = false"
        aria-label="Close sidebar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-6 h-6">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <div
      v-if="loadingNavigation"
      class="flex h-[90vh] w-full justify-center items-center py-8"
    >
      <Loader :loading="loadingNavigation" />
    </div>
    <template v-if="navigation?.length && !loadingNavigation">
      <ul class="navigation-list">
        <div>
          <li class="parent-menu flex flex-col w-full">
            <button
              class="flex justify-between items-center w-full text-[1rem] p-4 text-white hover:bg-[#292929] transition-all duration-200 bg-[#00B087] hover:bg-[#00B087]"
            >
              <span class="flex items-center">
                <ListBulletIcon class="size-6 text-white inline-block mr-6" />
                Dashboard
              </span>
            </button>
          </li>
          <li
            v-for="(item, index) in navigation"
            :key="index"
            class="parent-menu flex flex-col w-full"
          >
            <button
              v-if="item.children?.length"
              class="flex justify-between items-center w-full text-[1rem] p-4 text-white hover:bg-[#292929] transition-all duration-200"
              @click="toggleMenu(index)"
            >
              <span class="flex items-center">
                <ListBulletIcon class="size-6 text-white inline-block mr-6" />
                {{
                  item.name == "Dashboard" ? "Dashboard (legacy)" : item.name
                }}
              </span>
              <ChevronRightIcon
                class="size-4 text-white inline-block menu-icon transition-transform duration-100"
                :class="{ 'rotate-90': openIndex === index }"
                v-if="item.children?.length"
              />
            </button>
            <a
              v-else
              class="flex justify-between items-center w-full text-[1rem] p-4 text-white hover:bg-[#292929] transition-all duration-200"
              :href="appURL + item.to"
            >
              <span class="flex items-center">
                <ListBulletIcon class="size-6 text-white inline-block mr-6" />
                {{
                  item.name == "Dashboard" ? "Dashboard (legacy)" : item.name
                }}
              </span>
            </a>
            <transition
              name="collapse"
              @before-enter="beforeEnter"
              @enter="enter"
              @leave="leave"
            >
              <ul
                v-if="openIndex === index"
                class="children flex flex-col bg-[#292929]"
              >
                <li
                  v-for="(child, cIndex) in item.children"
                  :key="cIndex"
                  class="child-menu flex w-full border-b-[1px] border-[#3a3a3a] last-of-type:border-b-0"
                >
                  <a
                    class="flex items-center p-3 text-white text-[1rem] hover:bg-[#3a3a3a] w-full transition-all duration-200 pl-10"
                    :href="appURL + child.to"
                  >
                    {{ child.name }}
                  </a>
                </li>
              </ul>
            </transition>
          </li>

          <li
            v-if="permissions.includes('generate-drawing-sheet')"
            class="parent-menu flex flex-col w-full"
          >
            <a
              class="flex justify-between items-center w-full text-[1rem] p-4 text-white hover:bg-[#292929] transition-all duration-200"
              href="/generate-drawing-sheet/index.html"
            >
              <span class="flex items-center">
                <ListBulletIcon class="size-6 text-white inline-block mr-6" />
                Generate Drawing Sheet
              </span>
            </a>
          </li>
        </div>
        <button
          class="text-white text-center py-4 px-4 mx-auto bg-[#00b08750] hover:bg-[#00B087] duration-200 w-[90%] rounded-xl flex self-end"
          @click="store.logout"
        >
          <ArrowLeftStartOnRectangleIcon
            class="size-6 text-white inline-block"
          />
          <span class="mx-auto"> Logout </span>
        </button>
      </ul>
    </template>
    <slot />
  </aside>
</template>

<script setup lang="ts">
import Logo from "@/assets/img/images/web-logo.png";
import Loader from "@/components/Shared/Loader.vue";
import { openV2Application } from "@/composables";
import { NavigationMenuItem } from "@/models";
import { useMainStore } from "@/store";
import {
  ArrowLeftStartOnRectangleIcon,
  Bars3Icon,
  ChevronRightIcon,
  ListBulletIcon,
} from "@heroicons/vue/24/solid";
import { onClickOutside } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { defineProps, ref } from "vue";
const appURL = import.meta.env.VITE_VUE2_APP_URL;
defineProps<{ navigation: NavigationMenuItem[] }>();

const store = useMainStore();

const sidebarRef = ref<HTMLElement | null>(null);
const { mobileDevice, sidebarOpen, loadingNavigation, permissions } =
  storeToRefs(useMainStore());
const openIndex = ref<number | null>(null);

onClickOutside(sidebarRef, () => {
  mobileDevice.value && sidebarOpen.value && (sidebarOpen.value = false);
});

const toggleMenu = (index: number): void => {
  openIndex.value = openIndex.value === index ? null : index;
};

const beforeEnter = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.height = "0px";
};

const enter = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.transition = "height 0.1s ease-out";
  element.style.height = element.scrollHeight + "px";
};

const leave = (el: Element): void => {
  const element = el as HTMLElement;
  element.style.transition = "height 0.1s ease-in";
  element.style.height = "0px";
};
</script>

<style scoped>
.navigation-list {
  margin: 0;
  padding: 0 0 15px;
  list-style: none;
  height: calc(100% - 65px);
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
}

.collapse-enter-active,
.collapse-leave-active {
  overflow: hidden;
}
</style>
